import { useEffect, useState } from "react";
import axios from "axios";
import { Globe, RefreshCw, Save, AlertCircle, Mail, MessageSquare, History, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { useITPortalI18n } from "../../i18n/ITPortalI18nContext";

/**
 * IT Admin policy registry per country.
 *
 * Two policies are managed here:
 *   - Communication channels allowed (email / sms / both).
 *   - Required fields by local law (Tax Code / Birth Date / Birth Place).
 *
 * The data lives in `mongodb.country_config`; missing rows fall back to
 * defaults defined in `backend/routes/country_config.py`.
 */
export const CountryPolicyWidget = () => {
  const { t, locale } = useITPortalI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [dirty, setDirty] = useState({});   // { CC: { field: value, ... } }
  // Audit history modal state — populated on click. Stays local to the
  // widget; closing the modal drops the data so we re-fetch the next
  // time the admin opens it (cheap, low frequency interaction).
  const [historyCountry, setHistoryCountry] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/country-config`);
      setRows(r.data.countries || []);
      setDirty({});
    } catch {
      toast.error(locale === "IT" ? "Errore nel caricamento della configurazione paesi" : "Error loading country configuration");
    } finally {
      setLoading(false);
    }
  };

  // Mara CR 22/02/2026 — Audit trail viewer (GDPR evidence). Each row
  // shows BEFORE/AFTER snapshot + a flat list of changed fields with
  // the actor + timestamp. The endpoint returns up to 200 entries
  // newest first; we cap rendering at the default 50 to stay readable.
  const openHistory = async (country, countryLabel) => {
    setHistoryCountry({ code: country, label: countryLabel });
    setHistoryEntries([]);
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/country-config/${country}/history`, { params: { limit: 50 } });
      setHistoryEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (locale === "IT" ? "Errore caricamento storico" : "History load failed"));
      setHistoryEntries([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryCountry(null);
    setHistoryEntries([]);
  };

  useEffect(() => { load(); }, []);

  // Apply a local change to the dirty buffer (debounced save on button click).
  const setLocal = (code, patch) => {
    setDirty(prev => ({ ...prev, [code]: { ...(prev[code] || {}), ...patch } }));
  };

  const getEffective = (row) => {
    const d = dirty[row.country] || {};
    return {
      communication_channels: d.communication_channels ?? row.communication_channels,
      // Req. Mara Capelli 26/05/2026 punto 22: flag OTP per-paese
      otp_delivery_channel: d.otp_delivery_channel ?? row.otp_delivery_channel ?? "email",
      welcome_sms_enabled: d.welcome_sms_enabled ?? row.welcome_sms_enabled ?? true,
      required_fields: {
        ...(row.required_fields || {}),
        ...((d.required_fields) || {}),
      },
    };
  };

  const isDirty = (code) => Boolean(dirty[code]);

  const save = async (row) => {
    const payload = dirty[row.country];
    if (!payload) return;
    try {
      setSavingCode(row.country);
      await axios.put(`${API}/country-config/${row.country}?actor=it-admin`, payload);
      toast.success(`${row.flag} ${row.name}: ${locale === "IT" ? "policy aggiornata" : "policy updated"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (locale === "IT" ? "Errore salvataggio" : "Save failed"));
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <div className="bg-[#111] border border-cyan-600/30 rounded-2xl p-6" data-testid="country-policy-widget">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            <Globe size={24} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">{t.cp_widget_title}</h3>
            <p className="text-gray-500 text-xs mt-0.5">{t.cp_widget_subtitle}</p>
          </div>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-white transition flex items-center gap-2 text-sm" data-testid="country-policy-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          {t.cp_reload}
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-gray-400">
        <div className="flex items-center gap-1.5"><Mail size={12} className="text-blue-400" /> {t.cp_legend_email}</div>
        <div className="flex items-center gap-1.5"><MessageSquare size={12} className="text-lojack-blue-400" /> {t.cp_legend_sms}</div>
        <div className="flex items-center gap-1.5"><AlertCircle size={12} className="text-lojack-clause-400" /> {locale === "IT" ? "Obbligatorio per legge" : "Required by law"}</div>
      </div>

      {/* Country grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="country-policy-table">
          <thead>
            <tr className="text-left text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-800">
              <th className="py-2 pr-3">{t.cp_col_country}</th>
              <th className="py-2 pr-3">{t.cp_col_channels}</th>
              <th className="py-2 pr-3">{t.cp_col_otp}</th>
              <th className="py-2 pr-3 text-center" title={locale === "IT" ? "SMS inviato al cliente quando il concessionario lo crea" : "SMS sent when the dealer creates the customer"}>{locale === "IT" ? "SMS benvenuto" : "Welcome SMS"}</th>
              <th className="py-2 pr-3 text-center">{t.cp_col_tax_code}</th>
              <th className="py-2 pr-3 text-center">{t.cp_col_birth_date}</th>
              <th className="py-2 pr-3 text-center">{t.cp_col_birth_place}</th>
              <th className="py-2 pr-3 text-center">{t.cp_col_active}</th>
              <th className="py-2 pr-3 text-right">{locale === "IT" ? "Azioni" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const eff = getEffective(row);
              return (
                <tr key={row.country} className="border-b border-gray-800/50 hover:bg-white/[0.02]"
                    data-testid={`country-row-${row.country}`}>
                  <td className="py-3 pr-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="text-xl">{row.flag}</span>
                      <span className="text-white font-medium">{row.name}</span>
                      <span className="text-gray-600 text-[10px]">{row.country}</span>
                    </span>
                  </td>
                  <td className="py-3 pr-3">
                    <select
                      value={eff.communication_channels}
                      onChange={e => setLocal(row.country, { communication_channels: e.target.value })}
                      className="bg-black/40 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:border-cyan-500 outline-none"
                      data-testid={`country-channels-${row.country}`}>
                      <option value="both">📧📱 {t.cp_opt_both}</option>
                      <option value="email">📧 {t.cp_opt_email_only}</option>
                      <option value="sms">📱 {t.cp_opt_sms_only}</option>
                    </select>
                  </td>
                  {/* Req. Mara Capelli 26/05/2026 punto 22: OTP delivery */}
                  <td className="py-3 pr-3">
                    <select
                      value={eff.otp_delivery_channel}
                      onChange={e => setLocal(row.country, { otp_delivery_channel: e.target.value })}
                      className="bg-black/40 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:border-cyan-500 outline-none"
                      data-testid={`country-otp-${row.country}`}>
                      <option value="email">📧 {t.cp_opt_otp_email}</option>
                      <option value="sms">📱 {t.cp_opt_otp_sms}</option>
                      <option value="both">📧📱 {t.cp_opt_otp_both}</option>
                    </select>
                  </td>
                  <td className="py-3 pr-3 text-center">
                    <label className="inline-flex items-center justify-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(eff.welcome_sms_enabled)}
                        onChange={e => setLocal(row.country, { welcome_sms_enabled: e.target.checked })}
                        className="w-4 h-4 accent-cyan-500"
                        data-testid={`country-welcome-sms-${row.country}`}
                      />
                    </label>
                  </td>
                  {["tax_code", "birth_date", "birth_place"].map(field => (
                    <td key={field} className="py-3 pr-3 text-center">
                      <label className="inline-flex items-center justify-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(eff.required_fields[field])}
                          onChange={e => setLocal(row.country, {
                            required_fields: { ...(dirty[row.country]?.required_fields || {}), [field]: e.target.checked }
                          })}
                          className="w-4 h-4 accent-cyan-500"
                          data-testid={`country-${row.country}-${field}`}
                        />
                      </label>
                    </td>
                  ))}
                  <td className="py-3 pr-3 text-center">
                    {row.source === "custom"
                      ? <span className="text-[10px] text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 px-2 py-0.5 rounded">{locale === "IT" ? "PERSONALIZZATA" : "CUSTOM"}</span>
                      : <span className="text-[10px] text-gray-500 bg-gray-700/30 px-2 py-0.5 rounded">DEFAULT</span>}
                  </td>
                  <td className="py-3 pr-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => openHistory(row.country, `${row.flag} ${row.name}`)}
                        disabled={historyLoading}
                        title={locale === "IT" ? "Storico modifiche (audit GDPR)" : "Change history (GDPR audit)"}
                        className="px-2 py-1.5 rounded-lg text-xs font-medium transition inline-flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white"
                        data-testid={`country-history-${row.country}`}>
                        <History size={12} />
                      </button>
                      <button
                        onClick={() => save(row)}
                        disabled={!isDirty(row.country) || savingCode === row.country}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition inline-flex items-center gap-1.5
                          ${isDirty(row.country)
                            ? "bg-cyan-600 hover:bg-cyan-700 text-white"
                            : "bg-gray-800 text-gray-600 cursor-not-allowed"}`}
                        data-testid={`country-save-${row.country}`}>
                        {savingCode === row.country
                          ? <><div className="spinner-sm" /> {t.cp_saving}</>
                          : <><Save size={12} /> {locale === "IT" ? "Salva" : "Save"}</>}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {Object.keys(dirty).length > 0 && (
        <div className="mt-4 p-3 bg-lojack-clause-950/30 border border-lojack-clause-500/40 rounded-xl text-xs text-lojack-clause-300 flex items-center gap-2"
             data-testid="country-policy-unsaved">
          <AlertCircle size={14} />
          {locale === "IT"
            ? `Hai modifiche non salvate per ${Object.keys(dirty).length} paese/i — usa il bottone "Salva" su ogni riga.`
            : `You have unsaved changes for ${Object.keys(dirty).length} country/ies — use the "Save" button on each row.`}
        </div>
      )}

      {/* Audit history modal — Mara CR 22/02/2026 GDPR evidence */}
      {historyCountry && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={closeHistory}
          data-testid="country-history-modal-backdrop"
        >
          <div
            className="bg-[#0c0c0c] border border-cyan-600/30 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="country-history-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <div>
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <History size={16} className="text-cyan-400" />
                  {locale === "IT" ? "Storico modifiche policy" : "Policy change history"}
                </h4>
                <p className="text-gray-500 text-xs mt-0.5">{historyCountry.label}</p>
              </div>
              <button
                onClick={closeHistory}
                className="text-gray-500 hover:text-white"
                data-testid="country-history-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3">
              {historyLoading && (
                <div className="text-center text-gray-500 text-sm py-8" data-testid="country-history-loading">
                  <RefreshCw size={16} className="inline animate-spin mr-2" />
                  {locale === "IT" ? "Caricamento..." : "Loading..."}
                </div>
              )}
              {!historyLoading && historyEntries.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-8" data-testid="country-history-empty">
                  {locale === "IT"
                    ? "Nessuna modifica registrata per questo paese."
                    : "No policy changes recorded for this country yet."}
                </div>
              )}
              {!historyLoading && historyEntries.map((entry, idx) => (
                <div
                  key={`${entry.updated_at}-${idx}`}
                  className="bg-[#111] border border-gray-800 rounded-xl p-4 text-xs"
                  data-testid={`country-history-entry-${idx}`}
                >
                  <div className="flex items-center justify-between mb-2 text-[11px] text-gray-500">
                    <span>
                      {locale === "IT" ? "Modificato da" : "Updated by"}:{" "}
                      <span className="text-cyan-300 font-mono">{entry.updated_by || "—"}</span>
                    </span>
                    <span className="font-mono">{entry.updated_at}</span>
                  </div>
                  {entry.diff && entry.diff.length > 0 && (
                    <div className="space-y-1">
                      {entry.diff.map((d, di) => (
                        <div key={di} className="flex items-center gap-2 flex-wrap">
                          <span className="text-cyan-400 font-mono">{d.field}</span>
                          <span className="text-gray-500">:</span>
                          <span className="text-lojack-red-400 line-through font-mono">
                            {d.before === null || d.before === undefined ? "∅" : String(d.before)}
                          </span>
                          <span className="text-gray-500">→</span>
                          <span className="text-green-400 font-mono">{String(d.after)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryPolicyWidget;
